import {
  ArchiveRestore,
  CheckCircle2,
  Clipboard,
  History,
  LoaderCircle,
  Play,
  RotateCcw,
  ShieldAlert,
  Square,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HttpError } from '../api';
import { hasAuthGroup, isLocalDevelopmentAuth } from '../auth';
import {
  executeSecPoseReannotation,
  getSecPoseReannotationHistory,
  planSecPoseReannotation,
  runSecPoseReannotationJobAction,
  type SecPoseReannotationHistory,
  type SecPoseReannotationJobView,
  type SecPoseReannotationPlan,
} from '../secposeReannotation';
import { useStore } from '../store';

const PLATFORM_ADMIN_GROUP = 'authentik Admins';

function errorText(error: unknown): string {
  if (error instanceof HttpError) {
    try {
      const parsed = JSON.parse(error.message) as { detail?: unknown };
      if (typeof parsed.detail === 'string') return parsed.detail;
    } catch {
      // Fall through to the raw HTTP error text.
    }
  }
  return error instanceof Error ? error.message : '操作失败';
}

function dateTime(ms: number | null | undefined): string {
  return ms == null ? '—' : new Date(ms).toLocaleString();
}

export function SecPoseReannotationPanel({ episodeId }: { episodeId: string }) {
  const authUser = useStore((state) => state.authUser);
  const canMutate =
    isLocalDevelopmentAuth(authUser) || hasAuthGroup(authUser, PLATFORM_ADMIN_GROUP);
  const [reason, setReason] = useState('运营确认需要重新标注');
  const [confirm, setConfirm] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [plan, setPlan] = useState<SecPoseReannotationPlan | null>(null);
  const [job, setJob] = useState<SecPoseReannotationJobView | null>(null);
  const [history, setHistory] = useState<SecPoseReannotationHistory | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshHistory = useCallback(async () => {
    try {
      setHistory(await getSecPoseReannotationHistory(episodeId));
    } catch (cause) {
      setError(errorText(cause));
    }
  }, [episodeId]);

  useEffect(() => {
    setPlan(null);
    setJob(null);
    setConfirm('');
    setIdempotencyKey('');
    void refreshHistory();
  }, [refreshHistory]);

  const item = plan?.items[0] ?? null;
  const activeJob = useMemo(
    () => job ?? (history?.jobs[0] ? { job: history.jobs[0], items: [] } : null),
    [history, job]
  );

  async function handlePlan() {
    setLoading('plan');
    setError(null);
    setJob(null);
    try {
      const nextPlan = await planSecPoseReannotation({
        episode_ids: [episodeId],
        reason: reason.trim(),
        stats_policy: 'preserve',
      });
      setPlan(nextPlan);
      setConfirm('');
      setIdempotencyKey(crypto.randomUUID());
    } catch (cause) {
      setPlan(null);
      setError(errorText(cause));
    } finally {
      setLoading(null);
    }
  }

  async function handleExecute() {
    if (!plan) return;
    setLoading('execute');
    setError(null);
    try {
      const nextJob = await executeSecPoseReannotation({
        episode_ids: [episodeId],
        reason: plan.reason,
        stats_policy: 'preserve',
        plan_id: plan.plan_id,
        idempotency_key: idempotencyKey,
        confirm,
      });
      setJob(nextJob);
      await refreshHistory();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setLoading(null);
    }
  }

  async function handleJobAction(action: 'resume' | 'abort' | 'rollback') {
    if (!activeJob) return;
    setLoading(action);
    setError(null);
    try {
      const nextJob = await runSecPoseReannotationJobAction(activeJob.job.job_id, action);
      setJob(nextJob);
      await refreshHistory();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setLoading(null);
    }
  }

  return (
    <section className="border-t border-brand-border pt-8 space-y-6" aria-label="SecPose 重标管理">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-brand-text-primary">
            <ArchiveRestore className="h-5 w-5 text-brand-orange" />
            重标管理
          </h3>
          <p className="mt-1 text-sm text-brand-text-secondary">
            原标注和人员统计会保留，新任务将进入下一轮次。
          </p>
        </div>
        <div className="text-right text-xs text-brand-text-secondary">
          <div>当前轮次：{history?.current.secpose_cycle ?? '—'}</div>
          <div>当前状态：{history?.current.status ?? '—'}</div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <label className="space-y-1 text-sm text-brand-text-secondary">
          <span>重标原因</span>
          <textarea
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              setPlan(null);
            }}
            rows={2}
            maxLength={500}
            className="w-full resize-y rounded-md border border-brand-border bg-brand-dark px-3 py-2 text-brand-text-primary outline-none focus:border-brand-orange"
          />
        </label>
        <button
          type="button"
          onClick={() => void handlePlan()}
          disabled={loading != null || reason.trim().length === 0}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-brand-border px-4 text-sm font-medium text-brand-text-primary transition hover:border-brand-orange disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading === 'plan' ? (
            <LoaderCircle className="h-4 w-4 animate-spin" />
          ) : (
            <ShieldAlert className="h-4 w-4" />
          )}
          运行预检
        </button>
      </div>

      {error && (
        <div
          className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300"
          role="alert"
        >
          {error}
        </div>
      )}

      {plan && item && (
        <div className="space-y-4 border-l-2 border-brand-border pl-4">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            <span className={plan.eligible ? 'text-emerald-400' : 'text-red-300'}>
              {plan.eligible ? '预检通过' : '预检未通过'}
            </span>
            <span className="text-brand-text-secondary">原状态：{item.status ?? '—'}</span>
            <span className="text-brand-text-secondary">原轮次：{item.secpose_cycle ?? '—'}</span>
            <span className="text-brand-text-secondary">
              历史帧数：{item.annotation_frame_count ?? '—'}
            </span>
            <span className="text-brand-text-secondary">
              DDB：{item.ddb.row_exists && !item.ddb.secpose_done_present ? '可重标' : '需处理'}
            </span>
            <span className="text-brand-text-secondary">
              R2：{item.r2.exists ? '已找到' : '缺失'}
            </span>
          </div>
          {item.blockers.length > 0 && (
            <ul className="space-y-1 text-sm text-red-300">
              {item.blockers.map((blocker) => (
                <li key={`${blocker.reason}:${blocker.detail}`}>{blocker.detail}</li>
              ))}
            </ul>
          )}

          {plan.eligible && canMutate && (
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <code className="min-w-0 flex-1 break-all rounded-md bg-brand-dark px-3 py-2 text-xs text-brand-text-secondary">
                  {plan.confirm_phrase}
                </code>
                <button
                  type="button"
                  title="复制确认短语"
                  aria-label="复制确认短语"
                  onClick={() => void navigator.clipboard.writeText(plan.confirm_phrase)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-brand-border text-brand-text-secondary hover:text-white"
                >
                  <Clipboard className="h-4 w-4" />
                </button>
              </div>
              <label className="block space-y-1 text-sm text-brand-text-secondary">
                <span>输入上方确认短语</span>
                <input
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                  className="w-full rounded-md border border-brand-border bg-brand-dark px-3 py-2 font-mono text-xs text-brand-text-primary outline-none focus:border-brand-orange"
                />
              </label>
              <button
                type="button"
                onClick={() => void handleExecute()}
                disabled={loading != null || confirm !== plan.confirm_phrase}
                className="inline-flex h-10 items-center gap-2 rounded-md bg-brand-orange px-4 text-sm font-semibold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {loading === 'execute' ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                开始下一轮标注
              </button>
            </div>
          )}
          {plan.eligible && !canMutate && (
            <p className="text-sm text-brand-text-secondary">
              预检结果仅供查看。执行重标需要平台管理员权限。
            </p>
          )}
        </div>
      )}

      {activeJob && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-brand-border pt-4">
          <div className="text-sm text-brand-text-secondary">
            <div className="font-mono text-xs text-brand-text-primary">{activeJob.job.job_id}</div>
            <div className="mt-1">
              状态：{activeJob.job.status}；创建时间：{dateTime(activeJob.job.created_at_ms)}
            </div>
          </div>
          {canMutate && (
            <div className="flex flex-wrap gap-2">
              {activeJob.job.status === 'running' && (
                <>
                  <button
                    type="button"
                    onClick={() => void handleJobAction('resume')}
                    disabled={loading != null}
                    className="inline-flex h-9 items-center gap-2 rounded-md border border-brand-border px-3 text-sm text-brand-text-primary disabled:opacity-50"
                  >
                    <Play className="h-4 w-4" />
                    继续
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleJobAction('abort')}
                    disabled={loading != null}
                    className="inline-flex h-9 items-center gap-2 rounded-md border border-red-500/50 px-3 text-sm text-red-300 disabled:opacity-50"
                  >
                    <Square className="h-4 w-4" />
                    中止
                  </button>
                </>
              )}
              {activeJob.job.status === 'aborting' && (
                <button
                  type="button"
                  onClick={() => void handleJobAction('abort')}
                  disabled={loading != null}
                  className="inline-flex h-9 items-center gap-2 rounded-md border border-red-500/50 px-3 text-sm text-red-300 disabled:opacity-50"
                >
                  <Square className="h-4 w-4" />
                  继续中止
                </button>
              )}
              {activeJob.job.status === 'completed' && (
                <button
                  type="button"
                  onClick={() => void handleJobAction('rollback')}
                  disabled={loading != null}
                  className="inline-flex h-9 items-center gap-2 rounded-md border border-red-500/50 px-3 text-sm text-red-300 disabled:opacity-50"
                >
                  <RotateCcw className="h-4 w-4" />
                  回滚本次重标
                </button>
              )}
              {activeJob.job.status === 'rolling_back' && (
                <button
                  type="button"
                  onClick={() => void handleJobAction('rollback')}
                  disabled={loading != null}
                  className="inline-flex h-9 items-center gap-2 rounded-md border border-red-500/50 px-3 text-sm text-red-300 disabled:opacity-50"
                >
                  <RotateCcw className="h-4 w-4" />
                  继续回滚
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="border-t border-brand-border pt-4">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-brand-text-primary">
          <History className="h-4 w-4" />
          历史轮次
        </h4>
        {history?.history.length ? (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[920px] text-left text-xs">
              <thead className="text-brand-text-secondary">
                <tr className="border-b border-brand-border">
                  <th className="py-2 pr-4 font-medium">轮次</th>
                  <th className="py-2 pr-4 font-medium">类型</th>
                  <th className="py-2 pr-4 font-medium">标注人</th>
                  <th className="py-2 pr-4 font-medium">归档人</th>
                  <th className="py-2 pr-4 font-medium">归档时间</th>
                  <th className="py-2 pr-4 font-medium">原因</th>
                  <th className="py-2 font-medium">归档结果</th>
                </tr>
              </thead>
              <tbody>
                {history.history.map((row) => (
                  <tr
                    key={row.history_id}
                    className="border-b border-brand-border/60 text-brand-text-primary"
                  >
                    <td className="py-2 pr-4">{row.cycle}</td>
                    <td className="py-2 pr-4">{row.kind}</td>
                    <td className="py-2 pr-4">{row.operator}</td>
                    <td className="py-2 pr-4">{row.archived_by}</td>
                    <td className="py-2 pr-4">{dateTime(row.archived_at_ms)}</td>
                    <td className="py-2 pr-4">{row.reason}</td>
                    <td className="py-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <code className="max-w-56 truncate" title={row.archive_key}>
                          {row.archive_key}
                        </code>
                        <button
                          type="button"
                          className="shrink-0 text-brand-text-secondary transition hover:text-white"
                          title="复制归档结果路径"
                          aria-label={`复制轮次 ${row.cycle} 的归档结果路径`}
                          onClick={() => void navigator.clipboard.writeText(row.archive_key)}
                        >
                          <Clipboard className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-2 text-sm text-brand-text-secondary">
            <CheckCircle2 className="h-4 w-4" />
            暂无历史重标记录
          </div>
        )}
      </div>
    </section>
  );
}
